import { Fragment, useState, type CSSProperties, type DragEvent, type KeyboardEvent } from 'react';
import type { GradeDoDia as Grade, MarcacaoNaGrade, QuadraDaGrade } from '@arena/shared/schemas';
import { horaNoRelogio, MINUTOS_DO_DIA } from '@arena/shared/tempo';
import {
  aparenciaDoCard,
  horaNaArena,
  seloDoPagamento,
  subtituloDaMarcacao,
  tituloDaMarcacao,
} from './formatos.js';
import {
  ALTURA_DA_TRILHA,
  blocosLivres,
  faixasDeHora,
  horaDaTrilha,
  janelaDaGrade,
  posicaoNaGrade,
  trilhasDaJanela,
  type Posicao,
} from './layout.js';

/**
 * A grade de duas colunas por hora de `#v-agenda`.
 *
 * Três interações, cada uma condicionada à matriz — cosmético, como
 * todo `pode()` do front; quem decide é o servidor:
 *
 *   clicar num card       abre `#m-evento` (todo perfil com leitura)
 *   clicar num "livre"    abre `#m-reserva` já com quadra e horário
 *                         (quem pode criar)
 *   arrastar um card      remarca; o servidor revalida o conflito
 *                         (quem pode editar)
 *
 * Arrastar não funciona com teclado, e por isso não é o ÚNICO caminho:
 * o mesmo remarcar existe no formulário de `#m-evento`.
 *
 * A arena que fecha de madrugada tem a grade seguindo depois da
 * meia-noite: a linha tracejada das 00:00 é a virada, e o que vem abaixo
 * dela é a noite DESTE dia, não a manhã do seguinte.
 *
 * SEM dica de passar o mouse, a pedido da recepção: o card já diz
 * horário, quem reservou e a situação do pagamento, e a dica aparecia
 * tapando justamente o card vizinho que se quer comparar. O que ela
 * acrescentava — a quadra e "faz parte de uma série semanal" — está no
 * `aria-label`, que é o que o leitor de tela lê, e no modal que o clique
 * abre.
 */
export function GradeDoDia({
  grade,
  podeCriar,
  podeMover,
  onLivre,
  onAbrir,
  onMover,
}: {
  grade: Grade;
  podeCriar: boolean;
  podeMover: boolean;
  onLivre: (quadraId: string, hora: string) => void;
  onAbrir: (marcacao: MarcacaoNaGrade) => void;
  onMover: (marcacao: MarcacaoNaGrade, quadraId: string, hora: string) => void;
}) {
  const [arrastando, setArrastando] = useState<{
    marcacao: MarcacaoNaGrade;
    trilhas: number;
  } | null>(null);
  const [sobre, setSobre] = useState<{ quadraId: string; trilha: number } | null>(null);

  const janela = janelaDaGrade(grade.horarioFuncionamento);
  const trilhas = trilhasDaJanela(janela);
  const coluna = new Map(grade.quadras.map((q, i) => [q.id, i + 2]));
  const quadraPorId = new Map(grade.quadras.map((q) => [q.id, q]));
  const ultima = grade.quadras.length + 1;

  // A linha 1 é o cabeçalho das quadras; a trilha 0 é a linha 2.
  const linhas = (trilha: number, quantas: number) => `${trilha + 2} / span ${quantas}`;

  const posicionadas = grade.marcacoes.flatMap((marcacao) => {
    const posicao = posicaoNaGrade(marcacao, grade.data, grade.fuso, janela);
    return posicao && coluna.has(marcacao.quadraId) ? [{ marcacao, posicao }] : [];
  });

  const livres = blocosLivres(
    grade.quadras.map((q) => q.id),
    posicionadas.map((p) => ({ quadraId: p.marcacao.quadraId, posicao: p.posicao })),
    janela,
  );

  const estilo: CSSProperties = {
    gridTemplateColumns: `60px repeat(${grade.quadras.length}, minmax(0, 1fr))`,
    gridTemplateRows: `auto repeat(${trilhas}, ${ALTURA_DA_TRILHA}px)`,
  };

  const soltar = (evento: DragEvent, quadraId: string, trilha: number) => {
    evento.preventDefault();
    const origem = arrastando;
    setArrastando(null);
    setSobre(null);
    if (origem) onMover(origem.marcacao, quadraId, horaDaTrilha(trilha, janela));
  };

  return (
    <div
      className={['courts', 'posicionada', arrastando && 'arrastando'].filter(Boolean).join(' ')}
      style={estilo}
    >
      <div className="hd" style={{ borderRight: '1px solid var(--line)' }} />
      {grade.quadras.map((quadra, i) => (
        <div
          key={quadra.id}
          className="hd"
          style={i + 2 < ultima ? { borderRight: '1px solid var(--line)' } : undefined}
        >
          <i style={{ background: quadra.corPadrao }} />
          {quadra.nome} · {quadra.superficie}
        </div>
      ))}

      {faixasDeHora(janela).map((faixa) => {
        const virada = faixa.minuto === MINUTOS_DO_DIA;
        return (
          <Fragment key={faixa.minuto}>
            <div
              className={virada ? 'hr virada' : 'hr'}
              style={{ gridRow: linhas(faixa.trilhaInicial, faixa.trilhas), gridColumn: 1 }}
            >
              {faixa.rotulo}
              {virada && <small>madrugada</small>}
            </div>
            {grade.quadras.map((quadra, i) => (
              <div
                key={quadra.id}
                className={virada ? 'slot virada' : 'slot'}
                style={{
                  gridRow: linhas(faixa.trilhaInicial, faixa.trilhas),
                  gridColumn: i + 2,
                  ...(i + 2 < ultima ? { borderRight: '1px solid var(--line-soft)' } : {}),
                }}
              />
            ))}
          </Fragment>
        );
      })}

      {livres.map((bloco) => {
        const quadra = quadraPorId.get(bloco.quadraId);
        if (!quadra) return null;
        return (
          <div
            key={`livre-${bloco.quadraId}-${bloco.trilhaInicial}`}
            className="celula"
            style={{
              gridRow: linhas(bloco.trilhaInicial, bloco.trilhas),
              gridColumn: coluna.get(bloco.quadraId),
            }}
          >
            <HorarioLivre
              hora={bloco.hora}
              quadra={quadra}
              onEscolher={podeCriar ? () => onLivre(bloco.quadraId, bloco.hora) : undefined}
            />
          </div>
        );
      })}

      {posicionadas.map(({ marcacao, posicao }) => (
        <div
          key={marcacao.id}
          className="celula"
          style={{
            gridRow: linhas(posicao.trilhaInicial, posicao.trilhas),
            gridColumn: coluna.get(marcacao.quadraId),
          }}
        >
          <CardDaMarcacao
            marcacao={marcacao}
            posicao={posicao}
            quadra={quadraPorId.get(marcacao.quadraId)}
            fuso={grade.fuso}
            arrastavel={podeMover && !marcacao.canceladaEm}
            onAbrir={() => onAbrir(marcacao)}
            onArrastar={(ativo) =>
              setArrastando(ativo ? { marcacao, trilhas: posicao.trilhas } : null)
            }
          />
        </div>
      ))}

      {/* Os alvos de soltar só existem enquanto alguém arrasta. */}
      {arrastando &&
        grade.quadras.flatMap((quadra) =>
          Array.from({ length: trilhas }, (_, trilha) => (
            <div
              key={`alvo-${quadra.id}-${trilha}`}
              className="alvo"
              style={{ gridRow: linhas(trilha, 1), gridColumn: coluna.get(quadra.id) }}
              onDragOver={(evento) => {
                evento.preventDefault();
                evento.dataTransfer.dropEffect = 'move';
                if (sobre?.quadraId !== quadra.id || sobre.trilha !== trilha) {
                  setSobre({ quadraId: quadra.id, trilha });
                }
              }}
              onDrop={(evento) => soltar(evento, quadra.id, trilha)}
            />
          )),
        )}

      {arrastando && sobre && (
        <div
          className="alvo sobre"
          aria-hidden="true"
          style={{
            gridRow: linhas(sobre.trilha, Math.min(arrastando.trilhas, trilhas - sobre.trilha)),
            gridColumn: coluna.get(sobre.quadraId),
            pointerEvents: 'none',
            zIndex: 3,
          }}
        />
      )}
    </div>
  );
}

function CardDaMarcacao({
  marcacao,
  posicao,
  quadra,
  fuso,
  arrastavel,
  onAbrir,
  onArrastar,
}: {
  marcacao: MarcacaoNaGrade;
  posicao: Posicao;
  quadra: QuadraDaGrade | undefined;
  fuso: string;
  arrastavel: boolean;
  onAbrir: () => void;
  onArrastar: (ativo: boolean) => void;
}) {
  const inicio = horaNaArena(marcacao.inicio, fuso);
  const fim = horaNaArena(marcacao.fim, fuso);
  const titulo = tituloDaMarcacao(marcacao);
  const subtitulo = subtituloDaMarcacao(marcacao);
  // Ausente para quem vê a projeção básica: o Professor não vê cobrança.
  const selo = seloDoPagamento(marcacao.formaPagamento, marcacao.situacaoPagamento);
  const { classe, estilo } = aparenciaDoCard(marcacao, quadra);
  const curta = posicao.trilhas < 2;

  const abrirPeloTeclado = (evento: KeyboardEvent) => {
    if (evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault();
      onAbrir();
    }
  };

  return (
    <div
      className={[classe, curta && 'curta'].filter(Boolean).join(' ')}
      style={estilo}
      role="button"
      tabIndex={0}
      aria-label={[
        titulo,
        subtitulo,
        `das ${inicio} às ${fim}`,
        quadra?.nome,
        selo,
        marcacao.serieId ? 'série semanal' : null,
      ]
        .filter(Boolean)
        .join(', ')}
      draggable={arrastavel}
      onClick={onAbrir}
      onKeyDown={abrirPeloTeclado}
      onDragStart={(evento) => {
        evento.dataTransfer.setData('text/plain', marcacao.id);
        evento.dataTransfer.effectAllowed = 'move';
        // Adiado: mexer no estilo do elemento arrastado dentro do próprio
        // `dragstart` cancela o arrastar no Chrome.
        setTimeout(() => onArrastar(true), 0);
      }}
      onDragEnd={() => onArrastar(false)}
    >
      {selo && <span className="pay">{selo}</span>}
      <b>{curta ? `${inicio} · ${titulo}` : titulo}</b>
      {subtitulo && <small>{subtitulo}</small>}
    </div>
  );
}

function HorarioLivre({
  hora,
  quadra,
  onEscolher,
}: {
  hora: string;
  quadra: QuadraDaGrade;
  onEscolher: (() => void) | undefined;
}) {
  if (!onEscolher) {
    return (
      <div className="ev free" aria-label={`${quadra.nome} livre às ${horaNoRelogio(hora)}`}>
        livre
      </div>
    );
  }

  return (
    <div
      className="ev free"
      role="button"
      tabIndex={0}
      aria-label={`Reservar a ${quadra.nome} às ${horaNoRelogio(hora)}`}
      onClick={onEscolher}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter' || evento.key === ' ') {
          evento.preventDefault();
          onEscolher();
        }
      }}
    >
      livre
    </div>
  );
}
